import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Box, Link, Tooltip } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { Delete, Download, Schedule, ThumbUpAlt } from '@mui/icons-material';
import Currency from 'components/formaters/Currency';
import { typeDate } from 'components/mui/Formatters';
import { InvoiceModel, InvoiceStatus } from 'models/InvoiceModel';
import useInvoicesPaginated from 'modules/invoices/hooks/useInvoicesPaginated';
import useInvoiceModals from 'modules/invoices/hooks/useInvoiceModals';
import { InvoiceLink } from 'modules/invoices/common/Components';
import VoyageLink from 'modules/cars/common/VoyageLink';
import auth from 'recoil/atoms/auth';


type ForShipperProps = {
    status: InvoiceStatus,
}

const updatable = [
    InvoiceStatus.Unpaid,
    InvoiceStatus.Pending,
];

const ForShipperInner = ({status}: ForShipperProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const {openInvoiceConfirmationModal, confirmDeleteInvoice} = useInvoiceModals();
    const {invoices, total, loading, setPage, setFilter} = useInvoicesPaginated(status, 'shipper');

    const confirmButton = (invoice: InvoiceModel) => (
        <GridActionsCellItem
            icon={<ThumbUpAlt/>}
            label={t('confirm')}
            onClick={() => openInvoiceConfirmationModal(invoice, invoices).then(() => setPage({
                page: 0,
                pageSize: 100
            }))}
        />
    );

    // TODO replace this and other Link with component after https://github.com/mui/mui-x/issues/4654
    const viewButton = (invoice: InvoiceModel) => (
        <Link component="a" target="_blank" href={`${process.env.REACT_APP_API_HOST}/invoices/${invoice.uuid}`}>
            <GridActionsCellItem
                icon={<Download/>}
                label={t('view')}
            />
        </Link>
    );

    const deleteButton = (invoice: InvoiceModel) => (
        <GridActionsCellItem
            icon={<Delete/>}
            label={t('delete')}
            onClick={() => confirmDeleteInvoice(invoice).then(() => setPage({page: 0, pageSize: 100}))}
            showInMenu
        />
    );

    const columns: GridColDef<InvoiceModel>[] = [
        {
            field: 'payer',
            headerName: t('payer'),
            flex: 1,
            minWidth: 180,
            valueFormatter: params => params.value.name,
        },
        {
            field: 'number',
            headerName: t('number'),
            renderCell: params => <>
                <InvoiceLink invoice={params.row}/>
                {params.row.status === InvoiceStatus.Pending &&
                    <Tooltip title={t('awaiting_confirmation')} placement="top">
                        <Schedule
                            color="warning"
                            fontSize="small"
                            sx={{ml: 1}}
                        />
                    </Tooltip>
                }
            </>,
            flex: 1,
            minWidth: 150,
            filterable: false,
        },
        {
            field: 'grandTotal',
            headerName: t('amount'),
            type: 'number',
            width: 130,
            renderCell: ({value, row}) => (
                <Box textAlign="right">
                    <Currency value={value} currency={row.currency}/>
                    <br/>
                    {row.paid > 0 && row.paid < value && (
                        <Box sx={{color: 'text.disabled'}}>
                            {t('paid')} <Currency value={row.paid} currency={row.currency}/>
                        </Box>
                    )}
                </Box>
            ),
        },
        {
            field: status === InvoiceStatus.Paid ? 'paidAt' : 'dueDate',
            headerName: status === InvoiceStatus.Paid ? 'Paid at' : 'Due Date',
            flex: 1,
            minWidth: 100,
            ...typeDate,
        },
        {
            field: 'description',
            headerName: t('description'),
            renderCell: params => <>
                {params.row.type === 'freight' && <VoyageLink voyage={params.row.voyage}/>}
                {params.row.type === 'top_up' && (
                    <>{t('invoice_top_up')}</>
                )}
            </>,
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'actions',
            type: 'actions',
            getActions: params => {
                const act = [
                    viewButton(params.row),
                ];

                if (my.permissions.has('invoices update') && updatable.includes(status)) act.push(confirmButton(params.row));
                if (my.permissions.has('invoices delete') && status === InvoiceStatus.Unpaid) act.push(deleteButton(params.row));
                return act;
            },
        },
    ];

    return (
        <Box sx={{height: 'calc(100vh - 148px)', bgcolor: 'background.paper'}}>
            <DataGrid
                loading={loading}
                rowCount={total}
                pageSizeOptions={[100]}
                paginationMode="server"
                onPaginationModelChange={setPage}
                filterMode="server"
                onFilterModelChange={setFilter}
                rows={invoices}
                columns={columns}
                disableRowSelectionOnClick
            />
        </Box>
    );
};

const ForShipperTable = (p: ForShipperProps) => {
    return (
        <Suspense fallback={null}>
            <ForShipperInner {...p} />
        </Suspense>
    )
}

export default ForShipperTable;
